import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  ExportLink,
  Payment,
  PaymentDetail,
  PaymentListQuery,
  PaymentSummary,
  CreatePaymentRequest,
  CashPaymentRequest,
  UpdatePaymentStatusRequest,
} from '@/types'

// 1:1 with Schema_Specification Section 4.7 (payments / 2.7.x).
export const paymentsApi = {
  // 2.7.1
  list: (q: PaymentListQuery = {}) =>
    apiClient
      .get<ApiList<Payment>>('/payments', { params: withListDefaults(q) })
      .then(r => r.data),

  // Tenant-wide counts for the summary cards. List filters are applied by the
  // list endpoint when a KPI card is clicked.
  summary: () =>
    apiClient
      .get<ApiResponse<PaymentSummary>>('/payments/summary')
      .then(r => r.data),

  // List export (feedback #50). Honours the current search/status/method/date
  // filters; returns a signed download link (Payment_list_[YYYY-MM-DD].[ext]).
  export: (q: PaymentListQuery & { format?: 'xlsx' | 'csv' } = {}) =>
    apiClient
      .get<ApiResponse<ExportLink>>('/payments/export', { params: q })
      .then(r => r.data),

  // 2.7.6
  get: (id: string) =>
    apiClient
      .get<ApiResponse<PaymentDetail>>(`/payments/${id}`)
      .then(r => r.data),

  // 2.7.2 / 2.7.8 - manual record (bank transfer, cheque, Stripe online, ...).
  create: (body: CreatePaymentRequest) =>
    apiClient.post<ApiResponse<Payment>>('/payments', body).then(r => r.data),

  // 2.7.3 - cash (on-site flow surfaces here).
  cash: (body: CashPaymentRequest) =>
    apiClient
      .post<ApiResponse<Payment>>('/payments/cash', body)
      .then(r => r.data),

  // 2.7.5 - retry a failed Stripe / online payment.
  retry: (id: string) =>
    apiClient
      .post<ApiResponse<Payment>>(`/payments/${id}/retry`)
      .then(r => r.data),

  // 2.7.7 - update status (Completed / Failed / Refunded ...).
  updateStatus: (id: string, body: UpdatePaymentStatusRequest) =>
    apiClient
      .put<ApiResponse<Payment>>(`/payments/${id}/status`, body)
      .then(r => r.data),

  // 2.7.9 - upload proof of payment (multipart).
  uploadProof: (id: string, files: Array<File | Blob>) => {
    const form = new FormData()
    for (const file of files) {
      form.append('files[]', file)
    }
    return apiClient
      .post<ApiResponse<Payment>>(`/payments/${id}/proof`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  renameProof: (id: string, fileId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<Payment>
      >('/payments/' + id + '/proof/' + fileId + '/rename', { name })
      .then(r => r.data),
}

export default paymentsApi
